import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiError } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Sun, Moon, Bell, User, Shield, Clock, Eye, EyeOff, KeyRound, Camera, Settings2, Sparkles, Mail, CheckCircle2, XCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, api, updateUser, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const fileInputRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({
    timesheet_reminders: true,
    approval_notifications: true,
    project_assignments: true,
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // Admin system settings
  const [editWindowDays, setEditWindowDays] = useState('15');
  const [reminderDays, setReminderDays] = useState('3');
  const [reminderHour12, setReminderHour12] = useState('10');
  const [reminderMinute, setReminderMinute] = useState('00');
  const [reminderAmPm, setReminderAmPm] = useState('AM');
  const [savingSettings, setSavingSettings] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailResult, setTestEmailResult] = useState(null); // { success, message }
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    fetchNotifications();
    if (isAdmin) fetchSystemSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSystemSettings = async () => {
    try {
      const res = await api.get('/settings');
      setEditWindowDays(String(res.data.time_entry_edit_days ?? 15));
      setReminderDays(String(res.data.timesheet_approval_reminder_days ?? 3));
      const h24 = res.data.timesheet_reminder_hour ?? 10;
      setReminderHour12(String(h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24));
      setReminderAmPm(h24 < 12 ? 'AM' : 'PM');
      const m = res.data.timesheet_reminder_minute ?? 0;
      setReminderMinute(String(m).padStart(2, '0'));
    } catch { /* silent */ }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    setTestEmailResult(null);
    try {
      const res = await api.post('/settings/test-email', { to: testEmailTo || user?.email });
      setTestEmailResult({ success: true, message: res.data.message });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Unknown error';
      setTestEmailResult({ success: false, message: msg });
    } finally {
      setTestingEmail(false);
    }
  };

  const saveSystemSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/settings', {
        time_entry_edit_days: parseInt(editWindowDays),
        timesheet_approval_reminder_days: parseInt(reminderDays),
        timesheet_reminder_hour: reminderAmPm === 'AM'
          ? (parseInt(reminderHour12) === 12 ? 0 : parseInt(reminderHour12))
          : (parseInt(reminderHour12) === 12 ? 12 : parseInt(reminderHour12) + 12),
        timesheet_reminder_minute: parseInt(reminderMinute) || 0,
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (err) {
      console.error('Failed to fetch notifications');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 600 * 1024) {
      toast.error('Image too large. Max 600KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      try {
        await api.put('/users/me/profile-image', { image: base64 });
        updateUser({ profile_image: base64 });
        toast.success('Profile photo updated');
      } catch {
        toast.error('Failed to upload photo');
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveImage = async () => {
    try {
      await api.delete('/users/me/profile-image');
      updateUser({ profile_image: null });
      toast.success('Profile photo removed');
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.new_password)) {
      toast.error('Password must contain at least one special character');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await api.put(`/users/${user.id}/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password updated successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowCurrentPw(false);
      setShowNewPw(false);
      setShowConfirmPw(false);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to update password'));
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to update notifications');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      admin: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      manager: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
      team_member: 'bg-green-500/10 text-green-600 dark:text-green-400',
      finance_viewer: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      leadership_viewer: 'bg-accent/20 text-accent-foreground',
    };
    const labels = {
      super_admin: 'Super Admin', admin: 'Admin', manager: 'Team Manager',
      team_member: 'Team Member', finance_viewer: 'Finance Viewer', leadership_viewer: 'Leadership Viewer',
    };
    return <Badge className={styles[role]}>{labels[role] || role}</Badge>;
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-600/10 via-indigo-500/5 to-violet-400/10 p-6">
        <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
            <Settings2 className="w-7 h-7 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Account & Preferences</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">Settings</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-primary/60" />
              Manage your account and preferences
            </p>
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <Card data-tour="settings-profile">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="w-5 h-5" /> Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center gap-1">
              <div
                className="relative w-16 h-16 rounded-full overflow-hidden cursor-pointer group border-2 border-border hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
                title="Click to change profile photo"
              >
                {user?.profile_image ? (
                  <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-primary/10 flex items-center justify-center text-xl font-bold text-primary select-none">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Camera className="w-5 h-5 text-white" />
                </div>
              </div>
              {user?.profile_image && (
                <button onClick={handleRemoveImage} className="text-xs text-destructive hover:underline">
                  Remove
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            </div>
            <div>
              <div className="text-xl font-semibold">{user?.first_name} {user?.last_name}</div>
              <div className="text-muted-foreground">{user?.email}</div>
              <div className="mt-2 flex items-center gap-2">
                {getRoleBadge(user?.role)}
                <Badge variant="outline">{user?.department?.replace('_', ' ')}</Badge>
              </div>
            </div>
          </div>
          
          <div className="pt-4 border-t border-border grid grid-cols-2 gap-4">
            {user?.job_title && (
              <div>
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">Job Title</Label>
                <div className="font-medium mt-1">{user.job_title}</div>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">Employee ID</Label>
              <div className="font-mono font-medium mt-1">{user?.employee_id || '—'}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appearance Section */}
      <Card data-tour="settings-appearance">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />} Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Dark Mode</div>
              <div className="text-sm text-muted-foreground">Toggle between light and dark themes</div>
            </div>
            <div className="flex items-center gap-3">
              <Sun className="w-4 h-4 text-muted-foreground" />
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
              <Moon className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card data-tour="settings-notifications">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5" /> Notifications
              {unreadCount > 0 && (
                <Badge className="bg-accent text-accent-foreground">{unreadCount}</Badge>
              )}
            </CardTitle>
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn">
                Mark All Read
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Timesheet Reminders</div>
                <div className="text-sm text-muted-foreground">Get reminded to submit your timesheet</div>
              </div>
              <Switch
                checked={notificationSettings.timesheet_reminders}
                onCheckedChange={(v) => setNotificationSettings(prev => ({ ...prev, timesheet_reminders: v }))}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Approval Notifications</div>
                <div className="text-sm text-muted-foreground">Get notified when your timesheet is reviewed</div>
              </div>
              <Switch
                checked={notificationSettings.approval_notifications}
                onCheckedChange={(v) => setNotificationSettings(prev => ({ ...prev, approval_notifications: v }))}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Project Assignments</div>
                <div className="text-sm text-muted-foreground">Get notified when assigned to new projects</div>
              </div>
              <Switch
                checked={notificationSettings.project_assignments}
                onCheckedChange={(v) => setNotificationSettings(prev => ({ ...prev, project_assignments: v }))}
              />
            </div>
          </div>

          {/* Recent Notifications */}
          {notifications.length > 0 && (
            <div className="pt-4 border-t border-border">
              <div className="text-sm font-medium mb-3">Recent Notifications</div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {notifications.slice(0, 10).map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-3 rounded-sm border ${notification.is_read ? 'border-border bg-transparent' : 'border-accent/30 bg-accent/5'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-sm">{notification.title}</div>
                        <div className="text-sm text-muted-foreground">{notification.message}</div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card data-tour="settings-security">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5" /> Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Account Status</div>
              <div className="text-sm text-muted-foreground">Your account is active and in good standing</div>
            </div>
            <Badge className="bg-green-500/10 text-green-600">Active</Badge>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Member Since</div>
              <div className="text-sm text-muted-foreground">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
              </div>
            </div>
            <Clock className="w-5 h-5 text-muted-foreground" />
          </div>
          <Separator />
          <div>
            <div className="flex items-center gap-2 font-medium mb-3">
              <KeyRound className="w-4 h-4" /> Change Password
            </div>
            <form onSubmit={handlePasswordChange} className="space-y-3">
              <div>
                <Label>Current Password</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                    required
                    placeholder="Enter current password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    tabIndex={-1}
                  >
                    {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showNewPw ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                    required
                    placeholder="Min. 8 characters"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNewPw(!showNewPw)}
                    tabIndex={-1}
                  >
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordForm.new_password && passwordForm.new_password.length < 8 && (
                  <p className="text-xs text-destructive mt-1">Must be at least 8 characters</p>
                )}
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <div className="relative">
                  <Input
                    type={showConfirmPw ? 'text' : 'password'}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                    required
                    placeholder="Re-enter new password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirmPw(!showConfirmPw)}
                    tabIndex={-1}
                  >
                    {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                  <p className="text-xs text-destructive mt-1">Passwords do not match</p>
                )}
              </div>
              <Button type="submit" className="w-full">Update Password</Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Admin System Configuration */}
      {isAdmin && (
        <Card data-tour="settings-system-config">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" /> System Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2" data-tour="settings-edit-window">
              <Label className="text-base font-medium">Previous Time Entry Edit Window</Label>
              <p className="text-sm text-muted-foreground">
                How far back team members can create or edit time entries. Enter 0 for no limit. Admins are not affected.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="365"
                    value={editWindowDays}
                    onChange={e => setEditWindowDays(e.target.value === '' ? '' : String(parseInt(e.target.value) || 0))}
                    className="w-28 text-center font-mono"
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground break-words">
                e.g. 7 = 1 week · 14 = 2 weeks · 15 = 15 days · 30 = 1 month · 0 = no limit
              </p>
            </div>

            <Separator />

            <div className="space-y-2" data-tour="settings-reminder-config">
              <Label className="text-base font-medium">Timesheet Approval Reminder</Label>
              <p className="text-sm text-muted-foreground">
                Send a combined email reminder to managers if a submitted timesheet has not been approved after this many days. Set to 0 to disable reminders.
              </p>
              <div className="space-y-3 mt-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={reminderDays}
                    onChange={e => setReminderDays(e.target.value === '' ? '' : String(parseInt(e.target.value) || 0))}
                    className="w-24 text-center font-mono"
                  />
                  <span className="text-sm text-muted-foreground">days after submission</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">Send at</span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={reminderHour12}
                    onChange={e => {
                      const raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') { setReminderHour12(''); return; }
                      const v = parseInt(raw);
                      if (v >= 1 && v <= 12) setReminderHour12(raw);
                    }}
                    onBlur={() => {
                      const v = parseInt(reminderHour12);
                      if (!reminderHour12 || isNaN(v) || v < 1) setReminderHour12('1');
                    }}
                    className="w-14 text-center font-mono"
                  />
                  <span className="text-muted-foreground font-mono">:</span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={reminderMinute}
                    onChange={e => {
                      const raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') { setReminderMinute(''); return; }
                      const v = parseInt(raw);
                      if (v >= 0 && v <= 59) setReminderMinute(raw);
                    }}
                    onBlur={() => {
                      const v = parseInt(reminderMinute);
                      if (reminderMinute === '' || isNaN(v)) setReminderMinute('00');
                      else setReminderMinute(String(v).padStart(2, '0'));
                    }}
                    className="w-14 text-center font-mono"
                  />
                  <Select value={reminderAmPm} onValueChange={setReminderAmPm}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">every Monday</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground break-words">
                e.g. 7 days + 10:00 AM = reminder sent every Monday at 10 AM for timesheets pending over a week · 0 days = disabled
              </p>
            </div>

            <div className="pt-2">
              <Button onClick={saveSystemSettings} disabled={savingSettings || editWindowDays === '' || reminderDays === '' || reminderHour12 === ''}>
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="text-base font-medium flex items-center gap-2">
                <Mail className="w-4 h-4" /> Test Email (SMTP Diagnostics)
              </Label>
              <p className="text-sm text-muted-foreground">
                Send a test email to verify your SMTP configuration is working. The exact error will be shown here if it fails.
              </p>
              <div className="flex gap-2 mt-3">
                <Input
                  type="email"
                  placeholder={user?.email || 'recipient@example.com'}
                  value={testEmailTo}
                  onChange={e => { setTestEmailTo(e.target.value); setTestEmailResult(null); }}
                  className="max-w-xs"
                />
                <Button variant="outline" onClick={sendTestEmail} disabled={testingEmail}>
                  {testingEmail ? 'Sending…' : 'Send Test'}
                </Button>
              </div>
              {testEmailResult && (
                <div className={`flex items-start gap-2 mt-2 p-3 rounded-md text-sm border ${testEmailResult.success ? 'border-green-300 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400' : 'border-destructive/40 bg-destructive/5 text-destructive'}`}>
                  {testEmailResult.success
                    ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    : <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  <span className="break-all">{testEmailResult.message}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
